import { dataAtom, dataComposer } from "@frend-digital/centra-legacy";

import type { CentraProductServer } from "../atomicSetup";
import { baseAtomics } from "../atomicSetup";
import {
  getCare,
  getComingSoon,
  getComposition,
  getFitAdvice,
  getLabels,
  getLook,
  getMediaWithVideo,
  getOnlineExclusive,
  getPreorder,
  getPreorderDate,
  getSaleProductTagDisplayStatus,
  getSizeGuide,
  getStandardRelatedProducts,
  getSustainability,
  getSwatch,
  getType,
  getVariants,
} from "../localAtomics";

/**
 * Object containing the base atomic functions used to format a Centra product.
 * These functions are used to extract specific data from a CentraProductServer object.
 */

const {
  getName,
  getUri,
  getDescriptionHtml,
  getProductBreadcrumbs,
  getProductSizes,
  getDisplayPriceWithSale,
  getVariantName,
  getSku,
  getMetaDescription,
  getMetaTitle,
  getProduct,
  getMetaKeywords,
  getProductSku,
} = baseAtomics;

const formatSeo = (product: CentraProductServer) => {
  const metaTitle = dataAtom(getMetaTitle, 1, "")(product);
  const metaDescription = dataAtom(getMetaDescription, 1, "")(product);
  const metaKeywords = dataAtom(getMetaKeywords, 1, "")(product);

  const seo = dataComposer({
    metaTitle,
    metaDescription,
    metaKeywords,
  });

  return seo;
};

const formatProduct = (product: CentraProductServer) => {
  const name = dataAtom(getName, 2)(product);
  const itemId = dataAtom(getProduct, 2)(product);
  const variantName = dataAtom(getVariantName, 2)(product);
  const breadcrumbs = dataAtom(getProductBreadcrumbs, 2)(product);
  const prices = dataAtom(getDisplayPriceWithSale, 2)(product);
  const uri = dataAtom(getUri, 2)(product);
  const description = dataAtom(getDescriptionHtml, 2)(product);
  const sku = dataAtom(getSku, 2)(product);
  const productSku = dataAtom(getProductSku, 2)(product);
  const sizes = dataAtom(getProductSizes, 2)(product);
  const media = dataAtom(getMediaWithVideo, 2)(product);
  const variants = dataAtom(getVariants, 2)(product);
  const swatch = dataAtom(getSwatch, 2)(product);
  const composition = dataAtom(getComposition, 2)(product);
  const sustainability = dataAtom(getSustainability, 2)(product);
  const care = dataAtom(getCare, 2)(product);
  const fit = dataAtom(getFitAdvice, 2)(product);
  const relatedProducts = dataAtom(getStandardRelatedProducts, 2)(product);
  const look = dataAtom(getLook, 2)(product);
  const sizeguide = dataAtom(getSizeGuide, 2)(product);
  const type = dataAtom(getType, 2)(product);
  const comingSoon = dataAtom(getComingSoon, 2)(product);
  const preorder = dataAtom(getPreorder, 2)(product);
  const preorderDate = dataAtom(getPreorderDate, 2)(product);
  const onlineExclusive = dataAtom(getOnlineExclusive, 2)(product);
  const labels = dataAtom(getLabels, 2)(product);
  const seo = formatSeo(product);
  const displayPercentOrSale = dataAtom(
    getSaleProductTagDisplayStatus,
    2,
  )(product); // Placeholder for sale tag status

  return dataComposer({
    name,
    breadcrumbs,
    variantName,
    prices,
    uri,
    displayItemId: itemId,
    itemId,
    media,
    description,
    sku,
    productSku,
    sizes,
    variants,
    swatch,
    comingSoon,
    preorder,
    preorderDate,
    onlineExclusive,
    labels,
    sustainability,
    composition,
    care,
    fit,
    relatedProducts,
    sizeguide,
    look,
    type,
    seo,
    displayPercentOrSale,
  });
};

export default formatProduct;
